import React, { useState, useEffect } from "react";
import { Plus, Trash2, Edit, Package, User, Hash, X } from "lucide-react";

const API_BASE_URL = "https://hrms.gtplindia.com:8443";




export default function AssetAllocationSetup() {
    const assetCategories = [
        "Laptop", "Desktop", "Monitor", "Mobile Phone",
        "Tablet", "Vehicle", "Access Card", "Software License", "Other"
    ];
    const statuses = ["Available", "Assigned", "Under Maintenance", "Retired"];

    const [assets, setAssets] = useState([]);
    const [modal, setModal] = useState({
        open: false,
        isEdit: false,
        data: {
            id: null,
            asset_name: "",
            serial_number: "",
            category: "Laptop",
            status: "Available",
            description: "",
            assigned_emp_id: "",
            assigned_emp_name: ""
        }
    });

    useEffect(() => {
        fetchAssets();
    }, []);

    // ---------------- FETCH ASSETS ----------------
    const fetchAssets = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/assets`);
            const data = await res.json();
            setAssets(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Fetch error:", err);
            setAssets([]);
        }
    };

    // ---------------- SAVE ASSET ----------------
    const saveAsset = async () => {
        if (!modal.data.asset_name) {
            alert("Asset name is required");
            return;
        }

        const isAssigned = modal.data.status === "Assigned";

        const payload = {
            asset_name: modal.data.asset_name,
            serial_number: modal.data.serial_number || null,
            category: modal.data.category,
            status: modal.data.status,
            description: modal.data.description || null,

            // ✅ BACKEND-CORRECT FIELD NAMES
            assigned_emp_id: isAssigned ? modal.data.assigned_emp_id || null : null,
            assigned_emp_name: isAssigned ? modal.data.assigned_emp_name || null : null
        };

        try {
            const url = modal.isEdit
                ? `${API_BASE_URL}/assets/${modal.data.id}`
                : `${API_BASE_URL}/assets`;

            const res = await fetch(url, {
                method: modal.isEdit ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const result = await res.json();

            if (!res.ok) {
                console.error("Server error:", result);
                alert(result.detail || "Save failed");
                return;
            }

            setModal({ ...modal, open: false });
            fetchAssets();
        } catch (err) {
            console.error("Network error:", err);
            alert("Cannot connect to backend");
        }
    };

    // ---------------- DELETE ----------------
    const deleteAsset = async (id) => {
        if (!window.confirm("Delete asset?")) return;
        await fetch(`${API_BASE_URL}/assets/${id}`, { method: "DELETE" });
        fetchAssets();
    };

    return (
        <div className="p-10 bg-gray-50 min-h-screen font-roboto">
            {/* FONTS */}
            <style>
                {`
                  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
                  .font-poppins { font-family: 'Poppins', sans-serif; }
                  .font-roboto { font-family: 'Roboto', sans-serif; }
                  body { font-family: 'Roboto', sans-serif; }
                `}
            </style>

            {/* HEADER */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold font-poppins text-gray-800">
                        Asset Management
                    </h1>
                    <p className="text-gray-500 text-sm">
                        Track and allocate assets across the organization
                    </p>
                </div>
                <button
                    onClick={() =>
                        setModal({
                            open: true,
                            isEdit: false,
                            data: {
                                asset_name: "",
                                serial_number: "",
                                category: "Laptop",
                                status: "Available",
                                description: "",
                                assigned_emp_id: "",
                                assigned_emp_name: ""
                            }
                        })
                    }
                    className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-xl flex items-center font-poppins font-semibold"
                >
                    <Plus size={18} className="mr-2" /> Add Asset
                </button>
            </div>

            {/* GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {assets.map(asset => (
                    <div key={asset.id} className="bg-white p-6 rounded-2xl shadow border">
                        <div className="flex justify-between mb-3">
                            <Package className="text-teal-600" />
                            <span className="text-xs font-bold text-gray-600">
                                {asset.status}
                            </span>
                        </div>

                        <h3 className="font-bold font-poppins text-lg">
                            {asset.asset_name}
                        </h3>

                        <p className="text-sm text-gray-400">{asset.category}</p>

                        <div className="mt-3 text-sm flex items-center">
                            <User size={14} className="mr-2 text-gray-400" />
                            {asset.assigned_emp_name || "Unassigned"}
                        </div>

                        <div className="flex justify-between mt-5">
                            <button
                                onClick={() =>
                                    setModal({
                                        open: true,
                                        isEdit: true,
                                        data: {
                                            ...asset,
                                            assigned_emp_id: asset.assigned_emp_id || "",
                                            assigned_emp_name: asset.assigned_emp_name || ""
                                        }
                                    })
                                }
                                className="text-indigo-600 font-semibold text-sm flex items-center"
                            >
                                <Edit size={16} className="mr-1" /> Edit
                            </button>

                            <button
                                onClick={() => deleteAsset(asset.id)}
                                className="text-red-400 hover:text-red-600"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL */}
            {modal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-2xl w-full max-w-md relative">
                        <button
                            onClick={() => setModal({ ...modal, open: false })}
                            className="absolute top-4 right-4 text-gray-400"
                        >
                            <X />
                        </button>

                        <h2 className="font-poppins font-bold text-xl mb-6">
                            {modal.isEdit ? "Update Asset" : "Add Asset"}
                        </h2>

                        <input
                            placeholder="Asset Name"
                            className="w-full border p-3 rounded-xl mb-3"
                            value={modal.data.asset_name}
                            onChange={e =>
                                setModal({ ...modal, data: { ...modal.data, asset_name: e.target.value } })
                            }
                        />

                        <input
                            placeholder="Serial Number"
                            className="w-full border p-3 rounded-xl mb-3"
                            value={modal.data.serial_number}
                            onChange={e =>
                                setModal({ ...modal, data: { ...modal.data, serial_number: e.target.value } })
                            }
                        />

                        {modal.data.status === "Assigned" && (
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <input
                                    placeholder="Emp ID"
                                    className="border p-3 rounded-xl"
                                    value={modal.data.assigned_emp_id}
                                    onChange={e =>
                                        setModal({ ...modal, data: { ...modal.data, assigned_emp_id: e.target.value } })
                                    }
                                />
                                <input
                                    placeholder="Emp Name"
                                    className="border p-3 rounded-xl"
                                    value={modal.data.assigned_emp_name}
                                    onChange={e =>
                                        setModal({ ...modal, data: { ...modal.data, assigned_emp_name: e.target.value } })
                                    }
                                />
                            </div>
                        )}

                        <select
                            className="w-full border p-3 rounded-xl mb-3"
                            value={modal.data.status}
                            onChange={e =>
                                setModal({ ...modal, data: { ...modal.data, status: e.target.value } })
                            }
                        >
                            {statuses.map(s => (
                                <option key={s}>{s}</option>
                            ))}
                        </select>

                        <button
                            onClick={saveAsset}
                            className="w-full bg-teal-600 text-white py-3 rounded-xl font-poppins font-bold"
                        >
                            Save Asset
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
